import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Modal,
  LoadingOverlay,
  Button,
  Group,
  Divider,
  Text,
  ActionIcon,
  Code,
  Badge,
  Card,
} from '@mantine/core';
import { IconCheck, IconFilter, IconShield, IconX } from '@tabler/icons';
import { CustomQueryBuilder } from './CustomQueryBuilder';
import { useAuth0 } from '@auth0/auth0-react';
import type { Field, RuleGroupType } from 'react-querybuilder';
import { ExternalObjectConf } from '@bageltechnologies/bagel-common';
import { SourceIcon } from '../utils/SourceIcon';
import { DateInput } from '@mantine/dates';
import { useUpdateExternalObject } from '../hooks/useIntegrations';

export interface FilterPopupProps {
  opened: boolean;
  onClose: () => void;
  externalObj: ExternalObjectConf;
  integrationKey: string;
  onSave?: (startDate: Date | null, filters: RuleGroupType) => void;
}

// react-querybuilder stamps internal `id`s on the query group and every rule/subgroup, and
// those leak into what we persist. The backend filter contract is just { combinator, rules },
// so recursively drop `id` before saving to keep the stored payload clean.
const stripQueryIds = (node: any): any => {
  const clone: any = { ...node };
  delete clone.id;
  if (Array.isArray(clone.rules)) {
    clone.rules = clone.rules.map(stripQueryIds);
  }
  return clone;
};

// Count the actual leaf rules in a query, descending into nested groups — so a query that is
// empty (or only contains empty groups) reports 0. Used to block saving an empty filter.
const countLeafRules = (node: any): number => {
  if (!node || !Array.isArray(node.rules)) return 0;
  return node.rules.reduce(
    (sum: number, rule: any) => sum + (Array.isArray(rule?.rules) ? countLeafRules(rule) : 1),
    0,
  );
};

export const FilterPopup: React.FC<FilterPopupProps> = ({
  opened,
  onClose,
  externalObj,
  integrationKey,
  onSave,
}) => {
  const auth0 = useAuth0();
  const { mutateAsync: updateExternalObject } = useUpdateExternalObject();

  if (!integrationKey || !externalObj) return null;
  console.log({ externalObj });
  // Create a stable default query to avoid re-renders
  const defaultQuery = useCallback(
    () => ({
      combinator: 'and',
      rules: [],
    }),
    [],
  );

  // Track if this is the first render with this external object
  const initializedRef = useRef(false);

  // Cast to extended type
  const extendedObj = externalObj as ExternalObjectConf;

  // Initialize state with the external object's filter or default
  const [localFilterValue, setLocalFilterValue] = useState<RuleGroupType>(() => {
    const filters = extendedObj?.filters || defaultQuery();
    console.log('Initial filters:', JSON.stringify(filters, null, 2)); // Debug log with better formatting
    return filters;
  });
  const [hasFilterChanges, setHasFilterChanges] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [datePickerPortalTarget, setDatePickerPortalTarget] = useState<HTMLDivElement | null>(null);

  // Portal container for date picker dropdown so it renders above the modal
  useEffect(() => {
    if (!opened) return;
    const el = document.createElement('div');
    Object.assign(el.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      right: '0',
      bottom: '0',
      zIndex: '99999',
      pointerEvents: 'none',
    });
    document.body.appendChild(el);
    setDatePickerPortalTarget(el);
    return () => {
      if (el.parentNode) document.body.removeChild(el);
      setDatePickerPortalTarget(null);
    };
  }, [opened]);

  // Helper function to get first import date or default to 1 year ago
  const getFirstImportDateOrDefault = (firstImportDate?: string | Date | null): Date => {
    if (firstImportDate) {
      return new Date(firstImportDate);
    }
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    return oneYearAgo;
  };

  // Local state for firstImportDate to reflect updates immediately
  const [localFirstImportDate, setLocalFirstImportDate] = useState<Date | null>(() => {
    return getFirstImportDateOrDefault(extendedObj?.firstImportDate);
  });

  // Map fields to the format expected by react-querybuilder
  const queryBuilderFields = React.useMemo(() => {
    if (!extendedObj || !extendedObj.fieldsMeta) {
      return [];
    }

    return extendedObj.fieldsMeta.map((field: any) => {
      let valueEditorType = 'text';
      if (field.type === 'boolean') {
        valueEditorType = 'checkbox';
      } else if (field.type === 'number') {
        valueEditorType = 'number';
      } else if (field.type === 'date') {
        valueEditorType = 'date';
      }

      let operators = undefined;
      if (field.type === 'boolean') {
        operators = [
          { name: '=', label: 'is' },
          { name: '!=', label: 'is not' },
        ];
      }

      return {
        name: field.fieldPath || field.name || field.key,
        label: field.label || field.name || field.key,
        valueEditorType,
        operators,
      };
    }) as Field[];
  }, [extendedObj]);

  console.log({ extendedObj, queryBuilderFields });

  // Reset state when external object changes (instead of using useEffect)
  if (extendedObj && !initializedRef.current) {
    initializedRef.current = true;
    setLocalFilterValue(extendedObj.filters || defaultQuery());
    setHasFilterChanges(false);
    // Reset firstImportDate state
    setLocalFirstImportDate(getFirstImportDateOrDefault(extendedObj?.firstImportDate));
  }

  // Reset the initialization flag when the modal closes
  useEffect(() => {
    if (!opened) {
      initializedRef.current = false;
    }
  }, [opened]);

  // Use memoized handler to improve stability
  const handleLocalFilterChange = useCallback(
    (query: RuleGroupType) => {
      console.log('Filter changed:', JSON.stringify(query, null, 2));
      console.log('Previous filter value:', JSON.stringify(localFilterValue, null, 2));
      setLocalFilterValue(query);
      setHasFilterChanges(true);
    },
    [localFilterValue],
  );

  const handleSave = async () => {
    if (!externalObj) return;

    try {
      // Keep filter as is - don't convert to undefined (but strip react-querybuilder's ids).
      const filterValue = stripQueryIds(localFilterValue || defaultQuery());

      setIsLoading(true);

      // If onSave is provided, let parent handle the API call
      if (onSave) {
        onSave(localFirstImportDate, filterValue);
        setHasFilterChanges(false);
      } else {
        // Otherwise, update directly via API
        // Create updated object with the new filter
        const updatedObject = {
          ...externalObj,
          filter: filterValue,
        } as ExternalObjectConf;

        // Call the parent callback to save the mapping with the updated object
        if (updateExternalObject) {
          //@ts-ignore
          await updateExternalObject({
            key: integrationKey,
            originObjectKey: externalObj.originObject || externalObj.originObjectKey || '',
            updates: { filters: filterValue },
          });
        }

        setHasFilterChanges(false);
      }

      // Close the modal
      // onClose();
    } catch (error) {
      console.error('Failed to save filter:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImportDateChange = async (date: Date | null) => {
    if (!externalObj) return;

    try {
      setIsLoading(true);

      // Update local state immediately to reflect the change
      setLocalFirstImportDate(date);

      // If onSave is provided, let parent handle the API call
      if (onSave) {
        onSave(date, localFilterValue);
      } else {
        // Otherwise, update directly via API
        // Format date as ISO string (2025-11-05T18:30:00.000Z) for storage
        const dateISOString = date ? date.toISOString() : null;

        // Call the API to update the external object
        await updateExternalObject({
          key: integrationKey,
          originObjectKey: externalObj.originObject || externalObj.originObjectKey || '',
          updates: { firstImportDate: dateISOString },
        });
      }

      // Close the modal
      // onClose();
    } catch (error) {
      console.error('Failed to change first import date:', error);
      // Revert local state on error
      setLocalFirstImportDate(getFirstImportDateOrDefault(extendedObj?.firstImportDate));
    } finally {
      setIsLoading(false);
    }
  };

  // Use the default onClose handler provided by props
  const handleModalClose = () => {
    onClose();
  };

  // Filters are optional and can be cleared to empty — EXCEPT Slack ChannelsGroup, whose
  // ongoing sync depends on filters matching channels, so it requires at least one rule.
  const objectKey = externalObj?.originObjectKey || externalObj?.originObject || externalObj?.name;
  const filtersRequired = integrationKey === 'slack' && objectKey === 'ChannelsGroup';
  const mustAddFilterRule = filtersRequired && countLeafRules(localFilterValue) === 0;

  return (
    <Modal
      opened={opened}
      onClose={handleModalClose}
      centered
      withCloseButton={false}
      size="lg"
      padding={0}
      closeOnClickOutside
      closeOnEscape
      zIndex={500}
      styles={{ content: { overflow: 'visible' }, body: { overflow: 'visible' } }}
    >
      <Group p="xs" position="apart">
        <Group spacing={8}>
          <IconShield size={18} stroke={1.5} />
          <Text fw={500}>Filter guard</Text>
        </Group>

        <ActionIcon onClick={handleModalClose}>
          <IconX size={16} />
        </ActionIcon>
      </Group>
      <Divider />
      <LoadingOverlay visible={isLoading} overlayBlur={2} />

      <Box p={0}>
        <Box>
          <Box p="xs" py={10} mb="xs" w="100%">
            <DateInput
              label="Set First Import Date"
              description="Set the first import date for the object"
              value={localFirstImportDate}
              onChange={(date: Date | null) => handleImportDateChange(date)}
              required={false}
              placeholder="Select a date"
              size="sm"
              clearable
              popoverProps={{
                withinPortal: true,
                zIndex: 100000,
                position: 'bottom-start',
                ...(datePickerPortalTarget && { portalProps: { target: datePickerPortalTarget } }),
                styles: { dropdown: { pointerEvents: 'auto' } },
              }}
            />
          </Box>
        </Box>
        <Divider />
        <Box p="xs" py={0} mb="xs">
          <Group spacing={8} my="xs">
            <Text fw={500}>Filter records from</Text>

            <Card withBorder p={0} px={8}>
              <Group spacing={6}>
                <SourceIcon sourceName={integrationKey} width={16} height={16} />
                <Text>{externalObj?.label || 'Object'}</Text>
              </Group>
            </Card>
          </Group>
          {queryBuilderFields.length > 0 && (
            <CustomQueryBuilder
              fields={queryBuilderFields}
              query={localFilterValue || defaultQuery()}
              onQueryChange={handleLocalFilterChange}
              addRuleToNewGroups={false}
              autoSelectField={true}
              autoSelectOperator={true}
              showCombinatorsBetweenRules={false}
              showNotToggle={false}
              showCloneButtons={false}
              resetOnFieldChange={true}
              resetOnOperatorChange={false}
            />
          )}
          {queryBuilderFields.length === 0 && (
            <Text color="dimmed" align="center" py="md">
              No fields available for filtering. Please ensure this object has defined fields.
            </Text>
          )}
        </Box>
        <Divider />
        <Group position="apart" p="xs">
          <Text size="xs" color="dimmed">
            {mustAddFilterRule && queryBuilderFields.length > 0
              ? 'Channels Group requires at least one filter rule to save.'
              : ''}
          </Text>
          <Button
            onClick={handleSave}
            disabled={!hasFilterChanges || queryBuilderFields.length === 0 || mustAddFilterRule}
            leftIcon={<IconCheck size={16} />}
            color="indigo"
            size="xs"
          >
            Save Changes
          </Button>
        </Group>
      </Box>
    </Modal>
  );
};

export default FilterPopup;
